import React, { type ReactElement } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useAppSelector } from 'store/hooks'
import { getCompanyAge, pluralize } from 'utils/functions'

import { DecisionStatus, QuestionType } from '../../../vendor.types'

import styles from './DecisionMechanism.module.scss'
import QuestionRow from './QuestionRow'

const roundToTwoDecimalPlaces = (number: number): string => {
  if (Number.isInteger(number)) {
    return number.toString()
  } else {
    return number.toFixed(2).toString()
  }
}

const DecisionMechanism: React.FC = (): ReactElement => {
  const { palette } = useTheme()
  const { decisionDetails } = useAppSelector((state) => state.vendor)

  return decisionDetails ? (
    <div className={styles.container}>
      <QuestionRow
        index={1}
        questionType={QuestionType.COMPANY_AGE}
        status={decisionDetails.companyAge.status}
        questionName="Koliko dugo firma postoji"
        answer={
          decisionDetails.companyAge.status !== DecisionStatus.unknown
            ? getCompanyAge(decisionDetails.companyAge.value)
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.companyAge?.marginalBoundary
            ? getCompanyAge(decisionDetails.companyAge.marginalBoundary.toString())
            : '',
          satisfactoryBoundary: getCompanyAge(decisionDetails.companyAge.satisfactoryBoundary.toString()),
        }}
      />
      <QuestionRow
        index={2}
        questionType={QuestionType.EMPLOYEE_COUNT}
        status={decisionDetails.employeeCount.status}
        questionName="Broj zaposlenih"
        answer={
          decisionDetails.employeeCount.status !== DecisionStatus.unknown
            ? decisionDetails.employeeCount.value.toString() +
              ` zaposlen${pluralize(decisionDetails.employeeCount.value, '', 'ih')}`
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.employeeCount.marginalBoundary,
          satisfactoryBoundary: decisionDetails.employeeCount.satisfactoryBoundary,
        }}
      />
      <QuestionRow
        index={3}
        questionType={QuestionType.ACCOUNT_BLOCKED}
        status={decisionDetails.accountBlocked.status}
        questionName="Da li je firma u blokadi"
        answer={
          decisionDetails.accountBlocked.status !== DecisionStatus.unknown
            ? decisionDetails.accountBlocked.value
              ? 'Da'
              : 'Ne'
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.accountBlocked.marginalBoundary,
          satisfactoryBoundary: decisionDetails.accountBlocked.satisfactoryBoundary,
        }}
      />
      <QuestionRow
        index={4}
        questionType={QuestionType.ACCOUNT_BLOCKED_RECENTLY}
        status={decisionDetails.accountBlockedRecently.status}
        questionName="Da li je firma bila blokadi u poslednje 3 godine"
        answer={
          decisionDetails.accountBlockedRecently.status !== DecisionStatus.unknown
            ? decisionDetails.accountBlockedRecently.value
              ? 'Da'
              : 'Ne'
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.accountBlockedRecently.marginalBoundary,
          satisfactoryBoundary: decisionDetails.accountBlockedRecently.satisfactoryBoundary,
        }}
      />
      <QuestionRow
        index={5}
        questionType={QuestionType.AVERAGE_MARGIN}
        status={decisionDetails.averageMargin.status}
        questionName="Prosečna neto margina u poslednje 3 godine"
        answer={
          decisionDetails.averageMargin.status !== DecisionStatus.unknown
            ? roundToTwoDecimalPlaces(decisionDetails.averageMargin.value) + ' %'
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.averageMargin.marginalBoundary,
          satisfactoryBoundary: decisionDetails.averageMargin.satisfactoryBoundary,
        }}
      />
      <QuestionRow
        index={6}
        questionType={QuestionType.INCOME_TREND}
        status={decisionDetails.incomeTrend.status}
        questionName="Trend rasta prihoda"
        answer={
          decisionDetails.incomeTrend.status !== DecisionStatus.unknown
            ? decisionDetails.incomeTrend.value.toString()
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.incomeTrend.marginalBoundary,
          satisfactoryBoundary: decisionDetails.incomeTrend.satisfactoryBoundary,
        }}
      />
      <QuestionRow
        index={7}
        questionType={QuestionType.INDUSTRY}
        status={decisionDetails.industry.status}
        questionName="Šifra delatnosti"
        answer={
          decisionDetails.industry.status !== DecisionStatus.unknown
            ? decisionDetails.industry.value
            : 'Nema podataka'
        }
        boundaries={{
          marginalBoundary: decisionDetails.industry.marginalBoundary,
          satisfactoryBoundary: decisionDetails.industry.satisfactoryBoundary,
        }}
      />
      <div className={styles.statusInfo}>
        <div className={styles.satisfy} />
        <Typography variant="subtitle1">Zadovoljava</Typography>
        <div className={styles.partiallySatisfy} />
        <Typography variant="subtitle1">Na granici margine</Typography>
        <div className={styles.unsatisfy} />
        <Typography variant="subtitle1">Ne zadovoljava</Typography>
      </div>
    </div>
  ) : (
    <div className={styles.loader}>
      <CircularProgress
        size={35}
        sx={{ marginLeft: '4px', color: palette.white.main, alignSelf: 'center' }}
      />
    </div>
  )
}

export default DecisionMechanism
